import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssignmentCard = ({ assignment, onStartReview }) => {
  const daysLeft = Math.ceil((new Date(assignment?.reviewDeadline) - new Date()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysLeft < 0;
  const isUrgent = daysLeft <= 3 && daysLeft >= 0;

  const getDeadlineStyle = () => {
    if (isOverdue || isUrgent) return 'text-error bg-error-subtle';
    if (daysLeft <= 7) return 'text-warning bg-warning-subtle';
    return 'text-success bg-success-subtle';
  };

  const getStatusBadge = (status) => {
    const config = {
      completed: { icon: 'CheckCircle', label: 'Completed', style: 'bg-success-subtle text-success' },
      'in-progress': { icon: 'Clock', label: 'In Progress', style: 'bg-warning-subtle text-warning' },
    };
    const c = config[status] || { icon: 'FileText', label: 'New', style: 'bg-primary-subtle text-primary' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${c.style}`}>
        <Icon name={c.icon} size={12} />
        {c.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={`px-4 py-3 hover:bg-muted/30 transition-colors ${isOverdue ? 'border-l-2 border-l-error' : isUrgent ? 'border-l-2 border-l-warning' : ''}`}>
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground line-clamp-1">{assignment?.paperTitle}</h3>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
            <span className="truncate">{assignment?.conferenceName}</span>
            {assignment?.trackName && (
              <>
                <span>&middot;</span>
                <span className="truncate">{assignment?.trackName}</span>
              </>
            )}
          </div>
        </div>
        {getStatusBadge(assignment?.status)}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon name="User" size={12} />
        <span>{assignment?.authorName}</span>
        {assignment?.authorInstitution && (
          <>
            <span>&middot;</span>
            <span className="truncate">{assignment?.authorInstitution}</span>
          </>
        )}
      </div>

      {assignment?.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {assignment?.keywords?.slice(0, 3)?.map((keyword, idx) => (
            <span key={idx} className="px-1.5 py-0.5 bg-muted text-muted-foreground text-[11px]">
              {keyword}
            </span>
          ))}
          {assignment?.keywords?.length > 3 && (
            <span className="text-[11px] text-muted-foreground">+{assignment?.keywords?.length - 3}</span>
          )}
        </div>
      )}

      {assignment?.isDraft && assignment?.progress > 0 && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-0.5">
            <span>Progress</span>
            <span>{assignment?.progress}%</span>
          </div>
          <div className="w-full bg-muted h-1">
            <div className="bg-primary h-1 transition-all" style={{ width: `${assignment?.progress}%` }} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Due {formatDate(assignment?.reviewDeadline)}</span>
          <span className={`px-1.5 py-0.5 text-[11px] font-medium ${getDeadlineStyle()}`}>
            {isOverdue ? 'Overdue' : `${daysLeft}d left`}
          </span>
        </div>

        {assignment?.isDraft ? (
          <Button size="xs" onClick={() => onStartReview(assignment?.reviewId)} iconName="Edit" iconPosition="left">
            {assignment?.progress > 0 ? 'Continue' : 'Start'}
          </Button>
        ) : (
          <Button size="xs" variant="outline" onClick={() => onStartReview(assignment?.reviewId)} iconName="Eye" iconPosition="left">
            View
          </Button>
        )}
      </div>
    </div>
  );
};

export default AssignmentCard;
